// Company stores (S4 shop, 2026-10-12) — small pieces the shop pages share:
// the card / button classes the S3 store pages already use, the status pill,
// the money rows, and the signed-in person's cart.
import type { ReactNode } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { useMyStore } from "@/components/StoreGate";
import { useStoreCart } from "@/lib/store-cart";
import { money, orderStatusText, TONE_CLASS, type StoreOrder } from "@/lib/store-shop";

export const CARD = "rounded-lg border border-rule bg-white p-4 shadow-sm sm:p-6";
export const H2 = "mb-1 font-display text-lg font-bold tracking-tight";
export const PRIMARY =
  "inline-flex min-h-[44px] items-center justify-center rounded-md bg-ink px-5 text-sm font-semibold text-white transition-colors hover:bg-ink-700 disabled:opacity-50";
export const SECONDARY =
  "inline-flex min-h-[40px] items-center justify-center rounded-md border border-rule bg-white px-3.5 text-sm text-ink transition-colors hover:bg-canvas disabled:opacity-50";
export const INPUT =
  "w-full min-w-0 rounded-md border border-cream-300 bg-white px-3 py-2 text-sm text-brown-500 placeholder:text-[#8a8a85] focus:border-peach-400 focus:outline-none";
export const LINK = "font-semibold text-ink underline-offset-2 hover:underline";

/** The signed-in person's cart in their store. */
export function useMyCart() {
  const { user } = useAuth();
  const { data } = useMyStore();
  return useStoreCart(data?.store.id, user?.id);
}

export function StatusPill({ order }: { order: Pick<StoreOrder, "status" | "rejectedReason" | "canPay" | "paymentMethod"> }) {
  const s = orderStatusText(order);
  return (
    <span className={`inline-flex max-w-full items-center rounded px-2 py-0.5 text-xs font-semibold ${TONE_CLASS[s.tone]}`}>
      <span className="truncate">{s.text}</span>
    </span>
  );
}

export function MoneyRow({ label, value, strong = false }: { label: ReactNode; value: ReactNode; strong?: boolean }) {
  return (
    <div className={`flex items-baseline justify-between gap-4 ${strong ? "text-base font-semibold text-ink" : "text-sm text-brown-400"}`}>
      <dt>{label}</dt>
      <dd className="text-right tabular-nums">{value}</dd>
    </div>
  );
}

/** Items, shipping, tax and total of an order (only the numbers it has). */
export function OrderMoney({ order }: { order: StoreOrder }) {
  return (
    <dl className="space-y-1">
      {order.subtotal != null && <MoneyRow label="Items" value={money(order.subtotal)} />}
      {order.shipping != null && <MoneyRow label="Shipping" value={money(order.shipping)} />}
      {order.tax != null && order.tax > 0 && <MoneyRow label="Tax" value={money(order.tax)} />}
      {order.total != null && <MoneyRow label="Total" value={money(order.total)} strong />}
    </dl>
  );
}

export function Notice({ tone = "plain", children }: { tone?: "ok" | "warn" | "risk" | "plain"; children: ReactNode }) {
  const cls =
    tone === "ok"
      ? "border-ok/30 bg-ok-bg text-ok"
      : tone === "warn"
        ? "border-rule bg-warn-bg text-warn"
        : tone === "risk"
          ? "border-rust-200 bg-risk-bg text-risk"
          : "border-rule bg-white text-ink";
  return <div className={`rounded-md border px-4 py-3 text-sm ${cls}`}>{children}</div>;
}
